/**
 * Centrální registr parametrů aplikace.
 *
 * Každý parametr má: typovanou výchozí hodnotu, kategorii (hierarchicky),
 * úroveň komplexnosti (základní → vývojářská), popisek a jemnou vysvětlivku.
 * Efektivní hodnota se skládá: výchozí < globální přepis < přepis profilu.
 *
 * Citlivé parametry (sensitive: true) nikdy neopouštějí prohlížeč —
 * neukládají se do profilů, sdílecích odkazů ani exportů.
 */

export type ParamLevel = 'basic' | 'advanced' | 'expert' | 'dev';
export type ParamValue = boolean | number | string;

export const PARAM_LEVELS: ParamLevel[] = ['basic', 'advanced', 'expert', 'dev'];

export const PARAM_LEVEL_LABELS: Record<ParamLevel, string> = {
  basic: 'Základní',
  advanced: 'Pokročilé',
  expert: 'Expertní',
  dev: 'Vývojářské',
};

export interface ParamDef<T extends ParamValue = ParamValue> {
  label: string;
  /** Jemná vysvětlivka — „co to dělá a kdy to použít". */
  help?: string;
  /** Hierarchická kategorie, např. ['Výpočet', 'Auto a špička']. */
  category: string[];
  level: ParamLevel;
  type: 'boolean' | 'number' | 'select' | 'text';
  default: T;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: Array<{ value: string; label: string }>;
  /** Citlivá hodnota — jen lokálně, mimo profily/sdílení/export. */
  sensitive?: boolean;
}

/** Registr všech parametrů. Klíč = stabilní id parametru. */
export const PARAM_DEFS = {
  /* ---------------- Doporučení a řazení ---------------- */
  'weights.time': {
    label: 'Váha: čas',
    help: 'Jak moc má při doporučení trasy záležet na celkové době cesty.',
    category: ['Doporučení'],
    level: 'basic',
    type: 'number',
    default: 1,
    min: 0,
    max: 1,
    step: 0.05,
  },
  'weights.cost': {
    label: 'Váha: cena',
    help: 'Vyšší váha = levnější trasy se doporučují přednostně.',
    category: ['Doporučení'],
    level: 'basic',
    type: 'number',
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
  },
  'weights.comfort': {
    label: 'Váha: komfort',
    category: ['Doporučení'],
    level: 'basic',
    type: 'number',
    default: 0.3,
    min: 0,
    max: 1,
    step: 0.05,
  },
  'weights.emissions': {
    label: 'Váha: emise',
    category: ['Doporučení'],
    level: 'basic',
    type: 'number',
    default: 0.3,
    min: 0,
    max: 1,
    step: 0.05,
  },
  'sort.key': {
    label: 'Výchozí řazení tras',
    help: 'Podle čeho se trasy seřadí po spuštění.',
    category: ['Doporučení'],
    level: 'advanced',
    type: 'select',
    default: 'score',
    options: [
      { value: 'score', label: 'Doporučené' },
      { value: 'time', label: 'Nejrychlejší' },
      { value: 'cost', label: 'Nejlevnější' },
      { value: 'wait', label: 'Nejméně čekání' },
      { value: 'emissions', label: 'Nejekologičtější' },
      { value: 'distance', label: 'Nejkratší' },
      { value: 'comfort', label: 'Nejpohodlnější' },
      { value: 'transfers', label: 'Nejméně přestupů' },
    ],
  },
  'filter.carFree': {
    label: 'Jen trasy bez auta',
    help: 'Skryje varianty vyžadující vlastní auto.',
    category: ['Doporučení', 'Filtry'],
    level: 'advanced',
    type: 'boolean',
    default: false,
  },
  'filter.accessible': {
    label: 'Jen bezbariérové trasy',
    category: ['Doporučení', 'Filtry'],
    level: 'advanced',
    type: 'boolean',
    default: false,
  },

  /* ---------------- Datové zdroje: trasování ---------------- */
  'routing.online': {
    label: 'Reálné trasy ze sítě',
    help: 'Geometrie a délky tras z routovací služby. Vypnuto = jen odhadový model.',
    category: ['Datové zdroje', 'Trasování'],
    level: 'basic',
    type: 'boolean',
    default: true,
  },
  'routing.provider': {
    label: 'Routovací služba',
    help: 'Automaticky = OSRM (bez klíče), s klíčem lze přepnout na OpenRouteService.',
    category: ['Datové zdroje', 'Trasování'],
    level: 'advanced',
    type: 'select',
    default: 'auto',
    options: [
      { value: 'auto', label: 'Automaticky' },
      { value: 'osrm', label: 'OSRM (bez klíče)' },
      { value: 'ors', label: 'OpenRouteService (vyžaduje klíč)' },
    ],
  },
  'routing.useProviderDuration': {
    label: 'Čas jízdy autem ze služby',
    help: 'Doba jízdy autem se přebírá z routovací služby (zohledňuje typ silnic) místo průměrné rychlosti.',
    category: ['Datové zdroje', 'Trasování'],
    level: 'expert',
    type: 'boolean',
    default: true,
  },
  'routing.timeoutMs': {
    label: 'Timeout dotazu (ms)',
    category: ['Datové zdroje', 'Trasování'],
    level: 'dev',
    type: 'number',
    default: 8000,
    min: 1000,
    max: 30000,
    step: 500,
  },
  'geocode.online': {
    label: 'Online hledání adres',
    help: 'Našeptávač adres přes Nominatim (OpenStreetMap). Vypnuto = jen vestavěná místa a souřadnice.',
    category: ['Datové zdroje', 'Hledání adres'],
    level: 'basic',
    type: 'boolean',
    default: true,
  },
  'transit.liveEnabled': {
    label: 'Živé odjezdy (PID)',
    help: 'V detailu trasy MHD ukáže skutečné odjezdy se zpožděním z Golemio API. Vyžaduje bezplatný klíč a importovaný PID GTFS feed.',
    category: ['Datové zdroje', 'Živá data'],
    level: 'advanced',
    type: 'boolean',
    default: false,
  },
  'transit.golemioKey': {
    label: 'Golemio API klíč',
    help: 'Bezplatný klíč z api.golemio.cz. Lze zadat i přes VITE_GOLEMIO_API_KEY. Ukládá se jen v tomto prohlížeči.',
    category: ['Datové zdroje', 'Živá data'],
    level: 'advanced',
    type: 'text',
    default: '',
    sensitive: true,
  },

  /* ---------------- Výpočet: auto a špička ---------------- */
  'car.peakEnabled': {
    label: 'Model dopravní špičky',
    help: 'Ráno a odpoledne jede auto pomaleji. Vypněte, pokud jezdíte mimo město.',
    category: ['Výpočet', 'Auto a špička'],
    level: 'advanced',
    type: 'boolean',
    default: true,
  },
  'car.peakMorningFrom': {
    label: 'Ranní špička od (h)',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 7,
    min: 0,
    max: 24,
    step: 0.5,
    unit: 'h',
  },
  'car.peakMorningTo': {
    label: 'Ranní špička do (h)',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 9,
    min: 0,
    max: 24,
    step: 0.5,
    unit: 'h',
  },
  'car.peakMorningFactor': {
    label: 'Zpomalení ráno (×)',
    help: '1,35 = jízda trvá o 35 % déle než mimo špičku.',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 1.35,
    min: 1,
    max: 3,
    step: 0.05,
  },
  'car.peakAfternoonFrom': {
    label: 'Odpolední špička od (h)',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 15.5,
    min: 0,
    max: 24,
    step: 0.5,
    unit: 'h',
  },
  'car.peakAfternoonTo': {
    label: 'Odpolední špička do (h)',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 18,
    min: 0,
    max: 24,
    step: 0.5,
    unit: 'h',
  },
  'car.peakAfternoonFactor': {
    label: 'Zpomalení odpoledne (×)',
    category: ['Výpočet', 'Auto a špička'],
    level: 'expert',
    type: 'number',
    default: 1.4,
    min: 1,
    max: 3,
    step: 0.05,
  },

  /* ---------------- Výpočet: veřejná doprava ---------------- */
  'transit.fareCzk': {
    label: 'Cena jízdenky (Kč)',
    help: 'Použije se u linek bez vlastního tarifu (např. z GTFS importu).',
    category: ['Výpočet', 'Veřejná doprava'],
    level: 'basic',
    type: 'number',
    default: 30,
    min: 0,
    max: 500,
    step: 1,
    unit: 'Kč',
  },
  'transit.transferMin': {
    label: 'Rezerva na přestup (min)',
    category: ['Výpočet', 'Veřejná doprava'],
    level: 'advanced',
    type: 'number',
    default: 3,
    min: 0,
    max: 30,
    step: 0.5,
    unit: 'min',
  },
  'transit.maxAccessKm': {
    label: 'Max. docházka na zastávku (km)',
    category: ['Výpočet', 'Veřejná doprava'],
    level: 'advanced',
    type: 'number',
    default: 1.2,
    min: 0.2,
    max: 5,
    step: 0.1,
    unit: 'km',
  },
  'transit.maxTransfers': {
    label: 'Max. počet přestupů',
    category: ['Výpočet', 'Veřejná doprava'],
    level: 'expert',
    type: 'number',
    default: 2,
    min: 0,
    max: 5,
    step: 1,
  },

  /* ---------------- Mapa: zóny výhodnosti ---------------- */
  'zones.metric': {
    label: 'Výchozí metrika zón',
    help: 'Podle čeho se mapa vybarvuje: kde je který způsob dopravy nejvýhodnější.',
    category: ['Mapa', 'Zóny výhodnosti'],
    level: 'advanced',
    type: 'select',
    default: 'bestMode',
    options: [
      { value: 'bestMode', label: 'Nejlepší režim (čas)' },
      { value: 'weighted', label: 'Vážená kombinace' },
      { value: 'time', label: 'Celkový čas' },
      { value: 'wait', label: 'Čekání' },
      { value: 'cost', label: 'Cena' },
      { value: 'emissions', label: 'Emise CO₂' },
      { value: 'effort', label: 'Fyzická náročnost' },
      { value: 'comfort', label: 'Komfort' },
      { value: 'transfers', label: 'Přestupy' },
      { value: 'uncertainty', label: 'Nejistota odhadu' },
    ],
  },
  'zones.timeAware': {
    label: 'Zóny závislé na čase',
    help: 'Čekání na spoje se počítá pro simulovaný čas — v noci MHD „zešedne".',
    category: ['Mapa', 'Zóny výhodnosti'],
    level: 'expert',
    type: 'boolean',
    default: true,
  },
  'zones.resolution': {
    label: 'Rozlišení mřížky (sloupce)',
    help: 'Více = jemnější mapa, pomalejší výpočet. Složitost roste kvadraticky.',
    category: ['Mapa', 'Zóny výhodnosti'],
    level: 'expert',
    type: 'number',
    default: 56,
    min: 24,
    max: 96,
    step: 4,
  },
  'zones.opacity': {
    label: 'Průhlednost vrstvy',
    category: ['Mapa', 'Zóny výhodnosti'],
    level: 'advanced',
    type: 'number',
    default: 0.55,
    min: 0.1,
    max: 0.9,
    step: 0.05,
  },

  /* ---------------- Vzhled a nápověda ---------------- */
  'ui.showHints': {
    label: 'Zobrazovat vysvětlivky',
    help: 'Jemné info tečky u složitějších funkcí.',
    category: ['Vzhled'],
    level: 'basic',
    type: 'boolean',
    default: true,
  },

  /* ---------------- Citlivé (jen lokálně) ---------------- */
  'routing.osrmEndpoint': {
    label: 'Vlastní OSRM endpoint',
    help: 'Nechte prázdné pro veřejné servery. Ukládá se jen v tomto prohlížeči.',
    category: ['Datové zdroje', 'Trasování'],
    level: 'expert',
    type: 'text',
    default: '',
    sensitive: true,
  },
  'routing.orsApiKey': {
    label: 'OpenRouteService API klíč',
    help: 'Získáte zdarma na openrouteservice.org. Lze zadat i přes VITE_ORS_API_KEY. Ukládá se jen v tomto prohlížeči.',
    category: ['Datové zdroje', 'Trasování'],
    level: 'expert',
    type: 'text',
    default: '',
    sensitive: true,
  },
  'geocode.nominatimEndpoint': {
    label: 'Vlastní Nominatim endpoint',
    category: ['Datové zdroje', 'Hledání adres'],
    level: 'expert',
    type: 'text',
    default: '',
    sensitive: true,
  },
} as const satisfies Record<string, ParamDef>;

export type ParamId = keyof typeof PARAM_DEFS;
export type ParamValueOf<K extends ParamId> = (typeof PARAM_DEFS)[K]['default'];

export const PARAM_IDS = Object.keys(PARAM_DEFS) as ParamId[];

export function isParamId(id: string): id is ParamId {
  return Object.prototype.hasOwnProperty.call(PARAM_DEFS, id);
}

/** Mapa přepisů hodnot (globální nastavení nebo přepisy profilu). */
export type ParamOverrides = Partial<Record<ParamId, ParamValue>>;

/** Zvaliduje hodnotu vůči definici; nevalidní → undefined. */
export function sanitizeParamValue(id: ParamId, value: unknown): ParamValue | undefined {
  const def = PARAM_DEFS[id] as ParamDef;
  if (def.type === 'boolean') return typeof value === 'boolean' ? value : undefined;
  if (def.type === 'number') {
    const n = typeof value === 'number' ? value : NaN;
    if (!Number.isFinite(n)) return undefined;
    const lo = def.min ?? -Infinity;
    const hi = def.max ?? Infinity;
    return Math.min(hi, Math.max(lo, n));
  }
  if (def.type === 'select') {
    return typeof value === 'string' && def.options?.some((o) => o.value === value)
      ? value
      : undefined;
  }
  return typeof value === 'string' ? value : undefined;
}

/** Pročistí celou mapu přepisů (cizí klíče a nevalidní hodnoty zahodí). */
export function sanitizeOverrides(raw: unknown): ParamOverrides {
  const out: ParamOverrides = {};
  if (typeof raw !== 'object' || raw === null) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isParamId(k)) continue;
    const clean = sanitizeParamValue(k, v);
    if (clean !== undefined) out[k] = clean;
  }
  return out;
}

/** Typovaný přístup k efektivním hodnotám parametrů. */
export interface ResolvedParams {
  get<K extends ParamId>(id: K): ParamValueOf<K>;
  /** Odkud hodnota pochází (pro UI: zděděno × přepsáno). */
  sourceOf(id: ParamId): 'default' | 'global' | 'profile';
}

/**
 * Složení efektivních hodnot: výchozí < globální < profil.
 * Profil s inheritGlobal=false globální vrstvu přeskakuje.
 * Citlivé parametry se z profilů ignorují vždy.
 */
export function resolveParams(
  global: ParamOverrides,
  profile?: { overrides: ParamOverrides; inheritGlobal: boolean } | null,
): ResolvedParams {
  return {
    get<K extends ParamId>(id: K): ParamValueOf<K> {
      const def = PARAM_DEFS[id] as ParamDef;
      if (profile && !def.sensitive) {
        const pv = profile.overrides[id];
        if (pv !== undefined) {
          const clean = sanitizeParamValue(id, pv);
          if (clean !== undefined) return clean as ParamValueOf<K>;
        }
        if (!profile.inheritGlobal) return def.default as ParamValueOf<K>;
      }
      const gv = global[id];
      if (gv !== undefined) {
        const clean = sanitizeParamValue(id, gv);
        if (clean !== undefined) return clean as ParamValueOf<K>;
      }
      return def.default as ParamValueOf<K>;
    },
    sourceOf(id: ParamId): 'default' | 'global' | 'profile' {
      const def = PARAM_DEFS[id] as ParamDef;
      if (profile && !def.sensitive && profile.overrides[id] !== undefined) return 'profile';
      if ((!profile || profile.inheritGlobal || def.sensitive) && global[id] !== undefined) {
        return 'global';
      }
      return 'default';
    },
  };
}

/** Strom kategorií pro UI nastavení (rekurzivně, při zachování pořadí registru). */
export interface ParamCategoryNode {
  name: string;
  path: string[];
  params: ParamId[];
  children: ParamCategoryNode[];
}

export function buildCategoryTree(maxLevel: ParamLevel): ParamCategoryNode[] {
  const levelIdx = PARAM_LEVELS.indexOf(maxLevel);
  const roots: ParamCategoryNode[] = [];
  const find = (nodes: ParamCategoryNode[], path: string[]): ParamCategoryNode => {
    let current = nodes;
    let node: ParamCategoryNode | undefined;
    const walked: string[] = [];
    for (const part of path) {
      walked.push(part);
      node = current.find((n) => n.name === part);
      if (!node) {
        node = { name: part, path: [...walked], params: [], children: [] };
        current.push(node);
      }
      current = node.children;
    }
    return node!;
  };
  for (const id of PARAM_IDS) {
    const def = PARAM_DEFS[id] as ParamDef;
    if (PARAM_LEVELS.indexOf(def.level) > levelIdx) continue;
    find(roots, def.category).params.push(id);
  }
  // Odstranit prázdné větve (kategorie, jejichž parametry odfiltrovala úroveň).
  const prune = (nodes: ParamCategoryNode[]): ParamCategoryNode[] =>
    nodes
      .map((n) => ({ ...n, children: prune(n.children) }))
      .filter((n) => n.params.length > 0 || n.children.length > 0);
  return prune(roots);
}
